import React from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { CircleCheck, X } from "lucide-react";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

export interface SuccessDialogCstProps {
  open: boolean;
  title: string;
  description?: string;
  actionLabel?: string;
  icon?: React.ReactNode;
  onAction?: () => void;
  onClose: () => void;
  showClose?: boolean;
  children?: React.ReactNode;
}

const SuccessDialogCst: React.FC<SuccessDialogCstProps> = ({
  open,
  title,
  description,
  actionLabel,
  icon = <CircleCheck size={40} />,
  onAction,
  onClose,
  showClose = false,
  children,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: "600px",
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "16px",
          padding: "32px",
          color: theme.palette.text.primary,
        }}
      >
        {showClose && (
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{
              position: "absolute",
              top: 24,
              right: 24,
              color: theme.palette.text.primary,
            }}
          >
            <X size={20} />
          </IconButton>
        )}
        {icon}
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: 1.2,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{ fontSize: "16px", fontWeight: 400, lineHeight: 1.5 }}
          >
            {description}
          </Typography>
        )}
        {children}
        {actionLabel && (
          <>
            <Divider flexItem />
            <ButtonTertiary size="md" onClick={onAction ?? onClose}>
              {actionLabel}
            </ButtonTertiary>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default SuccessDialogCst;
